// Projects.js - Enhanced with more projects and better structure
import { motion } from 'framer-motion';
import { FiExternalLink, FiGithub } from 'react-icons/fi';
import './Projects.css';

const projects = [
  {
    id: 1,
    title: 'School Management System',
    subtitle: 'Enterprise Education Platform',
    description: 'Full-stack administration platform serving 2 client schools with 500+ users. Features role-based access control, attendance tracking, grade management, parent-teacher portals, and real-time analytics dashboards with Chart.js integration.',
    longDescription: 'Engineered enterprise-grade school administration platform with comprehensive modules for student enrollment, attendance monitoring, examination management, and academic performance analytics. Implemented multi-tier role-based access control, automated notification systems, and secure payment gateway integration.',
    tags: ['PHP', 'MySQL', 'JavaScript', 'Bootstrap', 'AJAX', 'Chart.js'],
    link: '#',
    liveLink: 'https://ranjansolution.com/portfolio/mount-carmel-convent-school',
    image: '/assets/school-management-system.webp',
    achievements: ['2 Active Deployments', '500+ Users', '40% Faster Load Times']
  },
  {
    id: 2,
    title: 'ExamKit Educational Platform',
    subtitle: 'Digital Learning Resource Hub',
    description: 'Scalable ed-tech platform hosting 500+ examination papers, solutions, and study materials. Advanced search functionality with filters for subject, year, and semester, user authentication, and personalized dashboards.',
    longDescription: 'Comprehensive educational platform providing centralized access to academic resources. Features include full-text search capabilities, bookmark functionality, download history tracking, and an administrative dashboard for content management with real-time statistics visualization.',
    tags: ['PHP', 'MySQL', 'JavaScript', 'Bootstrap' , 'AJAX'],
    link: '#',
    liveLink: 'https://examkit.ranjansolution.in',
    image: '/assets/examkit.webp',
    achievements: ['100+ Resources', 'Sub-second Search', 'Responsive Design']
  },
  {
    id: 3,
    title: 'Snake Game',
    subtitle: 'Classic Arcade Recreation',
    description: 'Interactive Snake game built with vanilla JavaScript. Features smooth controls, progressive difficulty, high score tracking, and responsive design for both desktop and mobile play.',
    tags: ['JavaScript', 'CSS3', 'HTML5', 'LocalStorage'],
    link: 'https://github.com/shyamranjan-sr/snake-game',
    liveLink: 'http://snakefun-shyamranjan.netlify.app/',
    image: 'https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=800&q=80',
    achievements: ['Smooth Controls', 'High Score System']
  },
  {
    id: 4,
    title: 'Tic-Tac-Toe Pro',
    subtitle: 'Modern Take on Classic Game',
    description: 'Enhanced Tic-Tac-Toe with sleek design, smooth animations, and intelligent win detection. Features two-player mode, game state persistence, and fully responsive interface.',
    tags: ['JavaScript', 'CSS3', 'HTML5', 'Animations'],
    link: 'https://github.com/shyamranjan-sr/tic-tac-toe',
    liveLink: 'https://tic-tak-toe-game-shyam.netlify.app/',
    image: '/assets/tic-tac-toe.webp',
    achievements: ['Smooth Animations', 'Mobile Optimized']
  },
  {
    id: 5,
    title: 'Connect 4 Challenge',
    subtitle: 'Classic Strategy Game',
    description: 'Interactive Connect 4 game with clean UI, responsive gameplay, and win detection algorithms. Two-player battle on a 7x6 grid with visual feedback and game reset functionality.',
    tags: ['JavaScript', 'CSS3', 'HTML5', 'Game Logic'],
    link: 'https://github.com/shyamranjan-sr/connect4',
    liveLink: 'https://connect4-shyam.netlify.app/',
    image: '/assets/connect4.webp',
    achievements: ['Win Detection', 'Clean UI']
  }
];

function Projects() {
  return (
    <section id="projects" className="projects section">
      <motion.div
        className="section-title-wrap"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="section-title section-title--inline">Latest Works</h2>
      </motion.div>
      
      <motion.p
        className="section-subtitle"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        Enterprise solutions and interactive applications I've built
      </motion.p>

      <div className="projects__grid">
        {projects.map((project, i) => (
          <motion.article
            key={project.id}
            className="projects__card glass"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 * i }}
            whileHover={{ y: -8 }}
          >
            <a
              href={project.liveLink || project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="projects__card-image-link"
              aria-label={`View ${project.title}`}
            >
              <img
                src={project.image}
                alt={`${project.title} - ${project.subtitle}`}
                className="projects__card-image"
                loading="lazy"
              />
              <span className="projects__card-image-overlay">
                <FiExternalLink aria-hidden />
                <span>View Live Project</span>
              </span>
            </a>
            
            <div className="projects__card-body">
              <span className="projects__card-num">
                {String(i + 1).padStart(2, '0')}
              </span>
              
              <span className="projects__card-subtitle">
                {project.subtitle}
              </span>
              
              <h3 className="projects__card-title">{project.title}</h3>
              
              <p className="projects__card-desc">{project.description}</p>
              
              <div className="projects__card-tags">
                {project.tags.map((tag) => (
                  <span key={tag} className="projects__tag">
                    #{tag}
                  </span>
                ))}
              </div>
              
              <div className="projects__card-links" style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <motion.a
                  href={project.liveLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="projects__card-link"
                  whileHover={{ x: 4 }}
                >
                  <FiExternalLink />
                  Live Demo
                </motion.a>
                
                <motion.a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="projects__card-link"
                  whileHover={{ x: 4 }}
                >
                  <FiGithub />
                  Source Code
                </motion.a>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

export default Projects;